import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  collection,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  increment,
} from "firebase/firestore";
import { db } from "../../firebase";
import { AuthContext } from "../../user/AuthContext";

import CollaborationSidebar from "../components/CollaborationSidebar";
import VersionHistory       from "../components/VersionHistory";
import { builtinTemplates } from "./Templates";

import "./PromptDetail.css";

export default function PromptDetail() {
  const { promptId } = useParams();
  const navigate     = useNavigate();
  const currentUser  = useContext(AuthContext);

  // template vs doc
  const [isTemplate, setIsTemplate] = useState(false);
  const [promptDoc,  setPromptDoc]  = useState(null);
  const [loading,    setLoading]    = useState(true);

  // editable
  const [title,    setTitle]    = useState("");
  const [bodyText, setBodyText] = useState("");

  // subcollections
  const [versions,   setVersions]   = useState([]);
  const [comments,   setComments]   = useState([]);
  const [commentText, setCommentText] = useState("");

  // buttons
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let unsubV, unsubC;

    (async function load() {
      // built-in template?
      const tpl = builtinTemplates.find(t => t.id === promptId);
      if (tpl) {
        setIsTemplate(true);
        setPromptDoc(tpl);
        setTitle(tpl.title || "");
        setBodyText(tpl.body ?? tpl.text ?? "");
        setLoading(false);
        return;
      }

      // firestore
      const ref = doc(db, "prompts", promptId);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        navigate("/prompts");
        return;
      }
      const data = snap.data();
      setPromptDoc({ id: snap.id, ...data });
      setTitle(data.title || "");
      setBodyText(data.body ?? data.text ?? "");

      // versions
      unsubV = onSnapshot(
        query(collection(ref, "versions"), orderBy("createdAt","desc")),
        qs => setVersions(qs.docs.map(d=>({ id:d.id, ...d.data() })))
      );
      // comments
      unsubC = onSnapshot(
        query(collection(ref, "comments"), orderBy("createdAt","asc")),
        qs => setComments(qs.docs.map(d=>({ id:d.id, ...d.data() })))
      );

      setLoading(false);
    })();

    return () => {
      unsubV?.();
      unsubC?.();
    };
  }, [promptId, navigate]);

  const handleSave = async () => {
    if (isTemplate || !currentUser) return;
    setSaving(true);
    await updateDoc(doc(db,"prompts",promptId), {
      title,
      text: bodyText,
      updatedAt: serverTimestamp(),
    });
    setSaving(false);
  };

  const handleDelete = async () => {
    if (isTemplate || !currentUser) return;
    if (!window.confirm("Delete this prompt?")) return;
    setDeleting(true);
    await deleteDoc(doc(db,"prompts",promptId));
    navigate("/prompts");
  };

  const likePrompt = async () => {
    if (isTemplate) return;
    await updateDoc(doc(db,"prompts",promptId), {
      likes: increment(1),
    });
  };

  const handleAddComment = async e => {
    e.preventDefault();
    if (!commentText.trim() || !currentUser) return;
    await addDoc(collection(db,"prompts",promptId,"comments"), {
      uid: currentUser.uid,
      text: commentText.trim(),
      likes: 0,
      createdAt: new Date(),
    });
    setCommentText("");
  };

  const likeComment = cid => updateDoc(
    doc(db,"prompts",promptId,"comments",cid),
    { likes: increment(1) }
  );

  if (loading) return <div className="loading-container">Loading…</div>;
  if (!promptDoc) {
    return (
      <div className="detail-not-found">
        Prompt not found.
        <button
          onClick={() => {
            window.scrollTo(0,0);
            navigate("/prompts");
          }}
        >
          ← Back
        </button>
      </div>
    );
  }

  const {
    department,
    visibility,
    resultMedia = [],
    ownerName,
    ownerEmail,
    likes: promptLikes,
  } = promptDoc;

  return (
    <div className="prompt-detail-page">
      {/* HEADER: back left, save/delete right */}
      <header className="detail-header">
        <button
  className="back-button"
  onClick={() => window.history.back()}
>
  ← Back
</button>

        {!isTemplate && currentUser.uid === promptDoc.ownerId && (
          <div className="header-actions">
            <button
              className="btn-save"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save"}
            </button>
            {/*
            <button
              className="btn-delete"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
            */}
        

          </div>
        )}
      </header>

      {/* TITLE & SUBTITLE */}
      <div className="title-section">
        {isTemplate ? (
          <h1 className="detail-title">{title}</h1>
        ) : (
          <input
            className="detail-title-input"
            value={title}
            onChange={e => setTitle(e.target.value)}
          />
        )}
      <p className="meta-line">
     <strong>Department:</strong> {department || "—"} &nbsp;|&nbsp;
     <strong>Visibility:</strong> {visibility || "—"}
   </p>
   {!isTemplate && (
     <p className="meta-line">
       <strong>Owned by:</strong> {ownerName || "Unknown"} ({ownerEmail || "—"})
       <span className="heart-icon">❤️ {promptLikes || 0}</span>
     </p>
      )}
      </div>

      {/* TWO-COLUMN LAYOUT */}
      <div className="detail-body-layout">
        {/* LEFT */}
        <div className="left-col">
          {!isTemplate && (
            <p className="text-muted owner">
              
            </p>
          )}

          {/* MEDIA */}
          {promptDoc.imageUrl && (
            <img
              src={promptDoc.imageUrl}
              alt="preview"
              className="detail-image"
            />
          )}
          {promptDoc.videoUrl && (
            <video
              src={promptDoc.videoUrl}
              controls
              className="detail-video"
            />
          )}
          {promptDoc.audioUrl && (
            <audio
              src={promptDoc.audioUrl}
              controls
              className="detail-audio"
            />
          )}

          {/* BODY */}
          <div className="detail-body">
            {isTemplate ? (
              <pre>{bodyText||"[ No body provided ]"}</pre>
            ) : (
              <textarea
                className="detail-body-textarea"
                rows={10}
                value={bodyText}
                onChange={e=>setBodyText(e.target.value)}
              />
            )}
          </div>

          {/* COMMENTS */}
          {!isTemplate && (
            <div className="comments-section">
              <h3>Comments</h3>
              <form onSubmit={handleAddComment}>
                <textarea
                  rows={3}
                  value={commentText}
                  onChange={e=>setCommentText(e.target.value)}
                />
                 {/*
                <button type="submit">Add Comment</button>
                */}
              </form>
              <ul className="comment-list">
                {comments.map(c=>(
                  <li key={c.id} className="comment-item">
                    <span>{c.text}</span>
                    <button onClick={()=>likeComment(c.id)}>
                      ❤️ {c.likes||0}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* RIGHT SIDEBAR */}
        {!isTemplate && (
          <aside className="detail-sidebar">
             <div className="card collaborators-card">
    <h3></h3>
     <CollaborationSidebar promptId={promptId} bare />
   </div>
<div className="card version-history-card">
    <h3></h3>
     <VersionHistory promptId={promptId} versions={versions} />
   </div>
            <div className="card metadata-card">
              <h3>All Metadata</h3>
              <ul>
                <li><strong>ID:</strong> {promptId}</li>
                <li><strong>Body:</strong> {bodyText||"—"}</li>
                <li><strong>Media Count:</strong> {resultMedia.length}</li>
              </ul>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
